import React, { useState, useRef } from 'react';
import { Upload, X, CheckCircle, Loader2 } from 'lucide-react';
import { addHolding, Lot } from '../../services/holdingsService';
import { cn } from '../../lib/utils';
import { safeToFixed } from '../../lib/formatters';
import { normalizeTicker } from '../../lib/normalizeTicker';

interface ImportTransactionsModalProps {
  uid: string;
  existingTickers: Set<string>;
  onClose: () => void;
  onImported: () => void;
}

type Step = 'upload' | 'preview' | 'importing' | 'done';

const DESC_FILTER_PATTERNS = [/INDEX/i, /\bFUND\b/i, /\bETF\b/i, /\bTRUST\b/i, /PORTFOLIO/i];
const TICKER_FILTER_PATTERNS = [/^[A-Z]{5}X$/]; // Schwab MF convention: 5 chars ending in X

function isEtfOrFund(ticker: string, description: string): boolean {
  return (
    DESC_FILTER_PATTERNS.some(p => p.test(description)) ||
    TICKER_FILTER_PATTERNS.some(p => p.test(ticker))
  );
}

interface ParsedPosition {
  ticker: string;
  description: string;
  shares: number;
  totalCostBasis: number;
  avgCostPerShare: number;
  hasZeroCost: boolean;
  action: 'add' | 'overwrite' | 'no-change';
  earliestBuyDate: string | null;
}

function clusterTransactions(
  txns: any[],
  existingTickers: Set<string>
): ParsedPosition[] {
  if (!txns || txns.length === 0) throw new Error('NO_TRANSACTIONS');

  const IGNORED_ACTIONS = new Set([
    'Journal', 'Qualified Dividend', 'Cash Dividend', 'MoneyLink Transfer'
  ]);

  const positions: Record<string, {
    description: string;
    totalShares: number;
    totalCostBasis: number;
    earliestBuyDate: string | null;
  }> = {};

  txns.forEach(t => {
    const raw = t.symbol;
    if (!raw || IGNORED_ACTIONS.has(t.action)) return;
    const sym = normalizeTicker(raw);
    if (isEtfOrFund(sym, t.description || '')) return; // skip ETFs/MFs

    const qty = t.quantity || 0;
    const amt = t.amount || 0;

    if (!positions[sym]) {
      positions[sym] = {
        description: t.description || sym,
        totalShares: 0,
        totalCostBasis: 0,
        earliestBuyDate: null,
      };
    }

    if (t.action === 'Buy') {
      positions[sym].totalShares += qty;
      positions[sym].totalCostBasis += Math.abs(amt);
      if (t.date && (!positions[sym].earliestBuyDate || t.date < positions[sym].earliestBuyDate)) {
        positions[sym].earliestBuyDate = t.date;
      }
    } else if (t.action === 'Sell') {
      if (positions[sym].totalShares > 0) {
        const sellRatio = qty / positions[sym].totalShares;
        positions[sym].totalCostBasis -= positions[sym].totalCostBasis * sellRatio;
      }
      positions[sym].totalShares -= qty;
    } else if (t.action === 'Stock Split') {
      positions[sym].totalShares += qty; // shares increase, cost basis unchanged
    }
  });

  return Object.entries(positions)
    .filter(([, p]) => p.totalShares > 0.001) // exclude fully sold positions
    .map(([sym, p]) => ({
      ticker: sym,
      description: p.description,
      shares: parseFloat(p.totalShares.toFixed(4)),
      totalCostBasis: parseFloat(p.totalCostBasis.toFixed(2)),
      avgCostPerShare: p.totalCostBasis > 0
        ? parseFloat((p.totalCostBasis / p.totalShares).toFixed(4))
        : 0,
      hasZeroCost: p.totalCostBasis === 0,
      action: existingTickers.has(sym) ? 'overwrite' : 'add',
      earliestBuyDate: p.earliestBuyDate ?? null,
    }));
}

export function ImportTransactionsModal({ uid, existingTickers, onClose, onImported }: ImportTransactionsModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [error, setError] = useState<string>('');
  const [parsed, setParsed] = useState<ParsedPosition[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  
  const [importProgress, setImportProgress] = useState(0);
  const [importingTicker, setImportingTicker] = useState('');
  const [failedTickers, setFailedTickers] = useState<string[]>([]);
  const [successCount, setSuccessCount] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const overwriteCount = parsed.filter(p => p.action === 'overwrite').length;
  const newCount = parsed.filter(p => p.action === 'add').length;
  const hasZeroCostAny = parsed.some(p => p.hasZeroCost);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setStep('upload');
      // Show some loading indicator if needed? Actually we can just keep state in upload or create a 'parsing' step.
      const res = await fetch('/api/portfolio/upload-transactions', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to parse file');
      }

      const transactions = await res.json();
      const parsedPositions = clusterTransactions(transactions, existingTickers);
      setParsed(parsedPositions.sort((a, b) => a.ticker.localeCompare(b.ticker)));
      setStep('preview');
    } catch (err: any) {
      console.error('File parsing error:', err);
      if (err.message === 'NO_TRANSACTIONS') {
        setError('No valid transaction data found in this file.');
      } else {
        setError(err.message || 'Invalid file. Please upload a Schwab transaction history export.');
      }
    } finally {
      // reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImport = async () => {
    setStep('importing');
    let sCount = 0;
    const fails: string[] = [];

    for (let i = 0; i < parsed.length; i++) {
      const position = parsed[i];
      setImportingTicker(position.ticker);
      setImportProgress(i);

      try {
        const res = await fetch(`/api/fmp/stable/profile?symbol=${encodeURIComponent(position.ticker)}`);
        let companyName = position.description;
        
        if (res.ok) {
          const profileData = await res.json();
          if (profileData?.[0]) {
            if (profileData[0].isEtf || profileData[0].isFund) {
              // skip silently
              continue;
            }
            companyName = profileData[0].companyName || companyName;
          }
        }

        const lot: Lot = {
          id: crypto.randomUUID(),
          type: 'buy',
          shares: position.shares,
          totalCostPaid: position.totalCostBasis,
          costPerShare: position.avgCostPerShare,
          datePurchased: position.earliestBuyDate
            ? new Date(position.earliestBuyDate + 'T12:00:00').toISOString()
            : new Date().toISOString(),
          source: 'schwab',
        };

        await addHolding(uid, {
          ticker: position.ticker,
          companyName,
          lots: [lot],
          dateAdded: new Date().toISOString(),
          source: 'schwab',
          notes: position.hasZeroCost
            ? 'Imported from Schwab. Cost basis unavailable (stock split) — please edit.'
            : 'Imported from Schwab transaction history.',
        });
        sCount++;
      } catch (err) {
        console.error(`Failed to import ${position.ticker}:`, err);
        fails.push(position.ticker);
      }
    }

    setSuccessCount(sCount);
    setFailedTickers(fails);
    setStep('done');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={step !== 'importing' ? onClose : undefined}></div>
      <div className="oi-overlay w-full max-w-2xl relative z-10 text-left flex flex-col max-h-[90vh]">
        {step !== 'importing' && step !== 'done' && (
          <button 
            onClick={onClose}
            className="absolute right-4 top-4 text-ink-500 hover:text-oxblood transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        <div className="p-6 border-b border-ink-200 flex-shrink-0 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-serif font-medium italic tracking-tight">Import Transactions</h2>
            <p className="text-sm text-ink-500 mt-1">Schwab JSON Export</p>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {step === 'upload' && (
            <div className="flex flex-col items-center justify-center p-12 bg-surface-2 rounded-lg border-2 border-dashed border-ink-200 hover:border-oxblood/50 hover:bg-surface transition-colors group">
              <div className="w-16 h-16 bg-surface rounded-lg flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                <Upload className="w-8 h-8 text-ink-500 group-hover:text-oxblood transition-colors" />
              </div>
              <h3 className="text-base font-medium text-ink mb-1 text-center">Drop your Schwab export here</h3>
              <p className="text-sm text-ink-500 max-w-sm text-center mb-6">
                Supports .csv, .xml, and .json exported from Schwab. Only individual stocks will be imported. ETFs and mutual funds are excluded automatically.
              </p>
              
              {error && (
                <div className="mb-6 p-4 w-full bg-danger-50 text-danger-700 text-sm rounded-md border border-danger-100 text-center">
                  {error}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.csv,.xml"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />
              <label 
                htmlFor="file-upload"
                className="bg-oxblood hover:bg-oxblood-800 text-sm font-medium text-paper px-6 py-3 rounded-lg cursor-pointer transition-colors"
              >
                Select File
              </label>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-4">
              <div className="flex gap-4 text-xs font-medium uppercase tracking-wider text-ink-500">
                <span>{parsed.length} stocks found</span>
                <span className="text-ink-300">·</span>
                <span className="text-success-700">{newCount} new positions</span>
                {overwriteCount > 0 && (
                  <>
                    <span className="text-ink-300">·</span>
                    <span className="text-warning-700">{overwriteCount} will be overwritten</span>
                  </>
                )}
              </div>

              <div className="space-y-2">
                {overwriteCount > 0 && (
                  <div className="bg-warning-50 border border-warning-100 rounded-md p-3 text-xs text-warning-700">
                    ⚠ <strong>{overwriteCount} existing position(s)</strong> will have their shares and cost basis
                    overwritten with values from this file. Your Keeper verdicts and notes will be preserved.
                  </div>
                )}
                {hasZeroCostAny && (
                  <div className="bg-surface-2 border border-ink-200 rounded-md p-3 text-xs text-ink-600">
                    ℹ Some positions were acquired via stock split and have no purchase price in this file.
                    They will be imported with $0 cost basis — use Edit to correct them after import.
                  </div>
                )}
              </div>

              <div className="bg-surface border border-ink-200 rounded-lg overflow-hidden">
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-left border-collapse text-sm">
                    <thead className="bg-surface-2 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 font-medium text-ink-500 text-xs uppercase tracking-wider">Ticker</th>
                        <th className="px-4 py-3 font-medium text-ink-500 text-xs uppercase tracking-wider text-right">Shares</th>
                        <th className="px-4 py-3 font-medium text-ink-500 text-xs uppercase tracking-wider text-right">Avg Cost/Share</th>
                        <th className="px-4 py-3 font-medium text-ink-500 text-xs uppercase tracking-wider text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-200">
                      {parsed.map((p) => (
                        <tr key={p.ticker} className="hover:bg-surface-2">
                          <td className="px-4 py-3 font-mono font-medium text-ink">{p.ticker}</td>
                          <td className="px-4 py-3 text-right text-ink-600 font-mono">{safeToFixed(p.shares, 4)}</td>
                          <td className="px-4 py-3 text-right text-ink-600 font-mono">${safeToFixed(p.avgCostPerShare, 2)}</td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex flex-wrap items-center justify-center gap-1">
                              {p.action === 'add' && (
                                <span className="inline-block bg-success-50 text-success-700 border border-success-100 text-[10px] uppercase font-medium tracking-wider px-2 py-0.5 rounded">
                                  New
                                </span>
                              )}
                              {p.action === 'overwrite' && (
                                <span className="inline-block bg-warning-50 text-warning-700 border border-warning-100 text-[10px] uppercase font-medium tracking-wider px-2 py-0.5 rounded">
                                  Overwrite
                                </span>
                              )}
                              {p.hasZeroCost && (
                                <span className="inline-block bg-surface-2 text-ink-600 border border-ink-200 text-[10px] uppercase font-medium tracking-wider px-2 py-0.5 rounded">
                                  No Cost Data
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {overwriteCount > 0 && (
                <label className="flex items-start gap-2 text-xs text-ink-600 cursor-pointer pt-2">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={e => setConfirmed(e.target.checked)}
                    className="mt-0.5 rounded border-ink-200 text-oxblood focus:ring-oxblood"
                  />
                  <span>
                    I understand that <strong>{overwriteCount} existing position(s)</strong> will be overwritten
                    with data from this file.
                  </span>
                </label>
              )}
            </div>
          )}

          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center p-12 text-center space-y-4">
              <Loader2 className="w-8 h-8 text-oxblood animate-spin" />
              <h3 className="text-lg font-medium">Importing Positions</h3>
              <p className="text-ink-500 font-mono text-sm">
                Importing {importingTicker} ({importProgress + 1} of {parsed.length})...
              </p>
            </div>
          )}

          {step === 'done' && (
            <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
              <CheckCircle className="w-16 h-16 text-success-600" />
              <h3 className="text-2xl font-medium font-serif italic">{successCount} positions imported successfully.</h3>
              {failedTickers.length > 0 && (
                <p className="text-sm text-danger-600">
                  Could not import: {failedTickers.join(', ')}. You can add these manually.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-ink-200 bg-surface-2 flex justify-end gap-3 rounded-b-lg flex-shrink-0">
          {step === 'preview' && (
            <>
              <button
                type="button"
                onClick={() => setStep('upload')}
                className="px-4 py-2 text-sm font-medium text-ink-600 hover:text-ink hover:bg-surface-2 rounded-lg transition-colors"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleImport}
                disabled={overwriteCount > 0 && !confirmed}
                className="px-4 py-2 text-sm font-medium text-paper bg-oxblood hover:bg-oxblood-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Import {parsed.length} Positions
              </button>
            </>
          )}

          {step === 'done' && (
            <button
              type="button"
              onClick={() => { onImported(); onClose(); }}
              className="px-6 py-2 text-sm font-medium text-paper bg-oxblood hover:bg-oxblood-800 rounded-lg transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
